import { useState, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { initializeMsalConfig } from '@/services/msalConfig';
import { AuthTokens } from '@/types/auth.types';

interface UseAzureAuthReturn {
  login: (usePopup?: boolean) => Promise<void>;
  logout: () => void;
  loading: boolean;
  error: string | null;
}

export const useAzureAuth = (): UseAzureAuthReturn => {
  const { instance, accounts } = useMsal();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const login = useCallback(async (usePopup: boolean = false) => {
    setLoading(true);
    setError(null);

    try {
      // Get properly initialized MSAL config and loginRequest
      const { loginRequest: dynamicLoginRequest } = await initializeMsalConfig();
      
      // Use redirect by default (more compatible), popup as optional
      let response;
      
      if (usePopup) {
        // Try popup method if explicitly requested
        try {
          response = await instance.loginPopup(dynamicLoginRequest);
        } catch (popupError: any) {
          // Fallback to redirect if popup fails
          await instance.loginRedirect(dynamicLoginRequest);
          return; // Redirect will handle the flow
        }
      } else {
        // Use redirect method (default and recommended)
        await instance.loginRedirect(dynamicLoginRequest);
        return; // Redirect will handle the flow, page will reload with tokens
      }

      if (response && response.idToken) {
        // Convert MSAL response to our token format
        const tokens: AuthTokens = {
          accessToken: response.accessToken,
          idToken: response.idToken,
          refreshToken: '', // MSAL handles refresh automatically
          expiresIn: response.expiresOn ? 
            Math.floor((response.expiresOn.getTime() - Date.now()) / 1000) : 3600,
          tokenType: 'Bearer'
        };

        // Store tokens and authentication method
        localStorage.setItem('auth_tokens', JSON.stringify(tokens));
        localStorage.setItem('auth_method', 'direct');
        localStorage.setItem('user_info', JSON.stringify({
          id: response.account?.localAccountId,
          email: response.account?.username,
          name: response.account?.name,
          tenantId: response.account?.tenantId
        }));

        // Redirect to dashboard or trigger state update
        window.location.href = '/dashboard';
      } else {
        throw new Error('Authentication successful but no tokens received');
      }

    } catch (err: any) {
      setError(err.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  }, [instance]);

  const logout = useCallback(() => {
    try {
      // Clear our app's auth data
      localStorage.removeItem('auth_tokens');
      localStorage.removeItem('auth_method');
      localStorage.removeItem('user_info');
      
      // Logout from Azure AD (MSAL will handle its own cache clearing)
      if (accounts.length > 0) {
        instance.logoutRedirect({
          account: accounts[0],
          postLogoutRedirectUri: window.location.origin
        });
      } else {
        window.location.href = '/';
      }
      
    } catch (err: any) {
      window.location.href = '/';
    }
  }, [instance, accounts]);

  return {
    login,
    logout,
    loading,
    error
  };
};